import { classColor, classOf, SHAPE_GLYPHS, ULT_CHARGE } from "@eldrun/content";
import { statusList, type StatusChip } from "./status-glyphs.ts";

/** O que o quadro do grupo lê de cada jogador (as partes públicas do estado). */
export interface PartySource {
  players: Iterable<[string, { name: string; slot: number; connected: boolean; classId?: string }]>;
  vitals: (
    id: string,
  ) => { hp: number; hpMax: number; patua: number; patuaMax: number; dead: boolean; ultCharge?: number; fallen?: boolean; fallLeft?: number; status?: number; statusBy?: number; statusEnding?: number } | undefined;
  position: (id: string) => { x: number; z: number } | undefined;
}

export interface PartyMember {
  id: string;
  name: string;
  /** Número do slot (1 a 4), o do hexágono. */
  n: number;
  /** Nome da classe (F64) e o ícone pela forma (F76). */
  className: string;
  classGlyph: string;
  /** Status no aliado (F76) e a cor da classe de quem aplicou o mais recente (a borda). */
  statuses: StatusChip[];
  statusColor: string;
  color: string;
  /** Vida e Égide de 0 a 1 (a Égide na escala da Vida, como na barra própria). */
  hp: number;
  patua: number;
  dead: boolean;
  /** Caído (F74): segundos até sangrar, ou `undefined` de pé. */
  fallen?: number;
  /** Ultimate carregada (F67): o pip no hexágono. */
  ultReady: boolean;
  connected: boolean;
  /** Metros até mim (arredondado), ou `undefined` sem posição. */
  distance: number | undefined;
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

/**
 * Quadros do grupo (F63, doc 09 §5.2): os outros jogadores, na ordem do número, com o número, a cor da
 * classe (F85; a de jogo, `color`), Vida, Égide, caído e a distância. Quem repete a classe divide a cor e
 * se distingue pelo número e pelo nome. Projeção pura (sem DOM).
 */
export function partyView(src: PartySource, me: string, color: (hex: string) => string = (hex) => hex): PartyMember[] {
  const mine = src.position(me);
  const players = [...src.players];
  const classBySlot = new Map(players.map(([, p]) => [p.slot, p.classId ?? ""]));
  const out: PartyMember[] = [];
  for (const [id, p] of players) {
    if (id === me) continue;
    const v = src.vitals(id);
    const pos = src.position(id);
    out.push({
      id,
      name: p.name,
      n: p.slot + 1,
      className: classOf(p.classId ?? "").name,
      classGlyph: SHAPE_GLYPHS[classOf(p.classId ?? "").shape],
      statuses: statusList(v?.status ?? 0, v?.statusEnding ?? 0),
      statusColor: color(classColor((v?.statusBy ?? 0) > 0 ? (classBySlot.get(v!.statusBy! - 1) ?? "") : (p.classId ?? ""))),
      color: color(classColor(p.classId ?? "")),
      hp: v && v.hpMax > 0 ? clamp01(v.hp / v.hpMax) : 0,
      patua: v && v.hpMax > 0 ? clamp01(v.patua / v.hpMax) : 0,
      dead: v?.dead === true,
      ...(v?.fallen ? { fallen: v.fallLeft ?? 0 } : {}),
      ultReady: v?.dead !== true && (v?.ultCharge ?? 0) >= ULT_CHARGE.max,
      connected: p.connected,
      distance: mine && pos ? Math.round(Math.hypot(pos.x - mine.x, pos.z - mine.z)) : undefined,
    });
  }
  return out.sort((a, b) => a.n - b.n);
}
